import { useCallback, useEffect, useRef, useState } from 'react'
import TabBar, { TABS } from './components/TabBar.jsx'
import ChipRow from './components/ChipRow.jsx'
import CaptureButton from './components/CaptureButton.jsx'
import Sidebar from './components/Sidebar.jsx'
import InstallHint from './components/InstallHint.jsx'
import RowContextMenu from './components/RowContextMenu.jsx'
import RowSwipe from './components/RowSwipe.jsx'
import { JumpContext } from './lib/jump.js'
import Sheet from './components/Sheet.jsx'
import { startPaneResize, useStoredPaneWidth } from './components/PaneResizer.jsx'
import { isConfigured } from './lib/supabase.js'
import { getSession, onAuthChange, signOut } from './lib/auth.js'
import {
  categoryOf, createStarterCategories, getCategories, isCashflow, STARTER_CATEGORIES,
} from './lib/notes.js'
import SignIn from './routes/SignIn.jsx'
import Today from './routes/Today.jsx'
import Calendar from './routes/Calendar.jsx'
import Reminders from './routes/Reminders.jsx'
import Log from './routes/Log.jsx'
import Search from './routes/Search.jsx'
import Inbox from './routes/Inbox.jsx'
import Category from './routes/Category.jsx'
import Money, { MONEY_SECTIONS } from './routes/Money.jsx'
import { useLayout } from './useLayout.js'

const SIDEBAR_WIDTH_KEY = 'mypersonalos.sidebarWidth'

/** Says what is missing and how to fix it, instead of a white screen. */
function NotConnected() {
  return (
    <div className="main">
      <div className="page-head">
        <span className="lbl">Setup</span>
        <h1 className="serif">Not connected yet</h1>
      </div>
      <div className="notice warn">
        <p style={{ margin: 0, fontSize: 14 }}>
          The app has no Supabase project to talk to. Copy <code>.env.example</code> to{' '}
          <code>.env.local</code> and fill in the two values, then restart{' '}
          <code>npm run dev</code>.
        </p>
      </div>
      <div className="card" style={{ marginTop: 12 }}>
        <div className="pad">
          <p className="lbl" style={{ margin: 0 }}>What goes in .env.local</p>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--ink-dim)' }}>
            <code>VITE_SUPABASE_URL</code> — the project URL.
            <br />
            <code>VITE_SUPABASE_ANON_KEY</code> — the <b>anon</b> key, the public one.
          </p>
          <p style={{ margin: '10px 0 0', fontSize: 13.5, color: 'var(--ink-dim)' }}>
            The service role key never leaves the Supabase dashboard. It does not belong in this
            file, this repo, or any chat.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * First run. The app never invents categories, so this is a button, not
 * something that happens to him.
 */
function FirstRun({ onDone, toast }) {
  const [busy, setBusy] = useState(false)
  return (
    <div className="main">
      <div className="page-head">
        <span className="lbl">First run</span>
        <h1 className="serif">Set up your categories</h1>
      </div>
      <div className="card">
        <div className="pad">
          <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--ink-dim)' }}>
            Five to start. Rename, add or delete any of them later — renaming keeps every link
            intact.
          </p>
          <ul className="catlist">
            {STARTER_CATEGORIES.map((c) => (
              <li key={c.name}>
                <i style={{ background: c.color }} />
                {c.name}
                {c.name === 'Cashflow' ? <em>opens your money screens</em> : null}
              </li>
            ))}
          </ul>
          <button
            className="btn"
            type="button"
            disabled={busy}
            style={{ marginTop: 16 }}
            onClick={async () => {
              setBusy(true)
              try {
                await createStarterCategories()
                onDone()
              } catch (e) {
                toast(e.message || 'Could not set those up.')
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? 'Setting up…' : 'Create these five'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Small account control. Sign-out needs a home, and it does not need a tab. */
function AccountButton({ email, onSignOut }) {
  const [open, setOpen] = useState(false)
  const initial = (email || '?').trim().charAt(0).toUpperCase()

  return (
    <>
      <button type="button" className="acct" aria-label="Account" onClick={() => setOpen(true)}>
        {initial}
      </button>
      {open ? (
        <Sheet title="Account" onClose={() => setOpen(false)}>
          <p style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--ink-dim)' }}>Signed in as</p>
          <p style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 600 }}>{email}</p>
          <button
            type="button"
            className="btn ghost"
            onClick={async () => {
              setOpen(false)
              await onSignOut()
            }}
          >
            Sign out
          </button>
        </Sheet>
      ) : null}
    </>
  )
}

/* Records that open on a category screen: find the category, then select. */
const CATEGORY_JUMPS = new Set(['topic', 'project'])

export default function App() {
  const layout = useLayout()
  /* One view at a time. A tab sets it to today/reminders/log/search; a chip
     sets it to a category id. The Today chip and the Today tab are the same
     place, which is why they share a value. */
  const [view, setView] = useState('today')
  const [msg, setMsg] = useState(null)
  const [session, setSession] = useState(null)
  const [checking, setChecking] = useState(isConfigured)
  const [categories, setCategories] = useState(null)
  const [catError, setCatError] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [moneySection, setMoneySection] = useState('cashflow')
  const [sidebarWidth, setSidebarWidth] = useStoredPaneWidth(SIDEBAR_WIDTH_KEY, {
    min: 200,
    max: 360,
    fallback: 240,
    /* Preserve the established first-run outcome: a missing key clamps to 200. */
    missingValue: 0,
  })
  const [sidebarOpen, setSidebarOpen] = useState(() => !layout.sidebarAutoCollapsed)
  const [contentScrolled, setContentScrolled] = useState(false)
  const [inlineTitleVisible, setInlineTitleVisible] = useState(false)
  const mainRef = useRef(null)
  const searchFocusPendingRef = useRef(false)
  /* A Log line can send you to the record it describes. `jump` is the record a
     route should open once it has loaded; `returnTo` is the single step back,
     including how far down you had scrolled, so the Log is not a trapdoor. */
  const [jump, setJump] = useState(null)
  const [returnTo, setReturnTo] = useState(null)
  const pendingScrollRef = useRef(null)

  const onSidebarChange = useCallback((target, section = 'none') => {
    setReturnTo(null)
    setJump(null)
    if (target === 'money') {
      const cashflowCategory = categories?.find((category) => isCashflow(category))
      setMoneySection(section)
      if (cashflowCategory) setView(cashflowCategory.id)
      return
    }
    setMoneySection('none')
    setView(target)
  }, [categories])

  /* Only types a route knows how to open are offered in the Log. */
  const JUMP_SECTIONS = {
    account: 'networth',
    build: 'builds',
    installment: 'plans',
    payout: 'cashflow',
    payable: 'cashflow',
  }

  /* Notes and projects open on their own category's screen rather than a
     money section, so the category is looked up first. */
  const canOpen = (type) => Boolean(JUMP_SECTIONS[type]) || CATEGORY_JUMPS.has(type)

  const jumpToRecord = useCallback(async (target, from) => {
    if (!target?.id || !canOpen(target.type)) return
    /* `reopen` is the record being left. Back re-opens it when the shell knows
       how to, and otherwise just returns to the screen it was on. */
    const origin = {
      view,
      moneySection,
      scrollTop: mainRef.current?.scrollTop ?? 0,
      reopen: from && canOpen(from.type) && from.id ? from : null,
    }
    if (CATEGORY_JUMPS.has(target.type)) {
      const categoryId = await categoryOf(target.type, target.id).catch(() => null)
      if (!categoryId) return
      setMoneySection('none')
      setView(categoryId)
      setReturnTo(origin)
      setJump(target)
      return
    }
    onSidebarChange('money', JUMP_SECTIONS[target.type])
    setReturnTo(origin)
    setJump(target)
  }, [view, moneySection, onSidebarChange])

  const goBack = useCallback(() => {
    if (!returnTo) return
    pendingScrollRef.current = returnTo.scrollTop
    setMoneySection(returnTo.moneySection)
    setView(returnTo.view)
    setReturnTo(null)
    setJump(returnTo.reopen ?? null)
  }, [returnTo])

  /* The list he came back to reloads, so its height arrives a few frames late.
     Keep trying until the scroller is tall enough to honour the position. */
  useEffect(() => {
    if (pendingScrollRef.current == null) return undefined
    const target = pendingScrollRef.current
    pendingScrollRef.current = null
    let frame = 0
    let tries = 0
    const settle = () => {
      const el = mainRef.current
      if (!el) return
      if (el.scrollHeight - el.clientHeight >= target || tries > 20) {
        el.scrollTop = target
        return
      }
      tries += 1
      frame = window.requestAnimationFrame(settle)
    }
    frame = window.requestAnimationFrame(settle)
    return () => window.cancelAnimationFrame(frame)
  }, [view, moneySection])

  useEffect(() => {
    if (layout.sidebarAutoCollapsed) setSidebarOpen(false)
  }, [layout.sidebarAutoCollapsed])

  useEffect(() => {
    function onKey(e) {
      if (layout.platform === 'mac' && e.metaKey && e.key === '\\') {
        e.preventDefault()
        setSidebarOpen((open) => !open)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [layout.platform])

  useEffect(() => {
    if (!searchFocusPendingRef.current || view !== 'search') return undefined
    searchFocusPendingRef.current = false
    const frame = window.requestAnimationFrame(() => {
      const field = document.getElementById('search-q')
      if (field && document.activeElement !== field) field.focus()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [view])

  useEffect(() => {
    if (layout.platform !== 'ios') {
      setInlineTitleVisible(false)
      return undefined
    }

    const root = mainRef.current
    if (!root) return undefined

    let observed = null
    let io = null

    function attach() {
      const heading = root.querySelector('.page-head h1')
      if (heading === observed) return
      io?.disconnect()
      observed = heading
      if (!heading) {
        setInlineTitleVisible(true)
        io = null
        return
      }

      setInlineTitleVisible(false)
      io = new IntersectionObserver(([entry]) => {
        setInlineTitleVisible(!entry.isIntersecting)
      }, {
        root,
        threshold: 0,
        rootMargin: '0px',
      })
      io.observe(heading)
    }

    attach()
    const mo = new MutationObserver(attach)
    mo.observe(root, { childList: true, subtree: true })
    return () => {
      mo.disconnect()
      io?.disconnect()
    }
  }, [layout.platform, view, moneySection, categories, refreshKey])

  useEffect(() => {
    function onCommandKey(e) {
      if (
        layout.platform !== 'mac' ||
        e.repeat ||
        !e.metaKey ||
        e.altKey ||
        e.ctrlKey ||
        e.shiftKey
      ) return

      const key = e.key.toLowerCase()
      const overlayOpen = Boolean(document.querySelector('.sheet-backdrop'))

      if (key === 'n') {
        e.preventDefault()
        if (overlayOpen) return
        const actions = Array.from(
          document.querySelectorAll('#mac-toolbar-view-actions button'),
        ).filter((element) => (
          !element.disabled &&
          element.getAttribute('aria-disabled') !== 'true' &&
          element.getClientRects().length > 0
        ))
        if (actions.length === 1) actions[0].click()
        return
      }

      if (key === 'f') {
        e.preventDefault()
        if (overlayOpen) return
        const searchField = document.getElementById('search-q')
        if (searchField) {
          if (document.activeElement !== searchField) searchField.focus()
          return
        }
        searchFocusPendingRef.current = true
        onSidebarChange('search')
        return
      }

      const tabIndex = Number(e.key) - 1
      const tab = Number.isInteger(tabIndex) ? TABS[tabIndex] : null
      if (!tab) return
      e.preventDefault()
      if (!overlayOpen) onSidebarChange(tab.key)
    }

    document.addEventListener('keydown', onCommandKey)
    return () => document.removeEventListener('keydown', onCommandKey)
  }, [layout.platform, onSidebarChange])

  /* Read the stored session once, then follow it. Without this the app would
     flash the sign-in screen on every reload before the session is restored. */
  useEffect(() => {
    if (!isConfigured) return undefined
    let alive = true
    getSession()
      .then((s) => { if (alive) setSession(s) })
      .catch(() => { if (alive) setSession(null) })
      .finally(() => { if (alive) setChecking(false) })
    const off = onAuthChange((s) => { if (alive) setSession(s) })
    return () => { alive = false; off() }
  }, [])

  /* The category list barely changes, yet every open waited on it before any
     screen could even start fetching. The last copy is shown at once and
     refreshed behind it, which takes a full round trip off every cold open.
     Categories are names and colours only; no money is cached here. */
  const userId = session?.user?.id ?? null
  const categoriesCacheKey = userId ? 'mypersonalos.categories.' + userId : null
  const showingCacheRef = useRef(false)

  const loadCategories = useCallback(async () => {
    setCatError(null)
    try {
      const fresh = await getCategories()
      setCategories(fresh)
      try { if (categoriesCacheKey) localStorage.setItem(categoriesCacheKey, JSON.stringify(fresh)) } catch { /* private mode */ }
    } catch (e) {
      /* With a cached list on screen, a failed refresh is not worth a banner. */
      if (showingCacheRef.current) return
      setCatError(e.message || String(e))
      setCategories([])
    }
  }, [categoriesCacheKey])

  useEffect(() => {
    if (!categoriesCacheKey) return
    try {
      const cached = JSON.parse(localStorage.getItem(categoriesCacheKey) || 'null')
      if (Array.isArray(cached) && cached.length) {
        showingCacheRef.current = true
        setCategories((current) => current ?? cached)
      }
    } catch { /* unreadable cache: just fetch */ }
    loadCategories()
  }, [categoriesCacheKey, loadCategories])

  const toast = useCallback((text) => {
    setMsg(text)
    window.clearTimeout(toast._t)
    toast._t = window.setTimeout(() => setMsg(null), 2600)
  }, [])

  const refresh = useCallback(() => setRefreshKey((n) => n + 1), [])

  const handleSignOut = useCallback(async () => {
    try {
      await signOut()
    } catch (e) {
      toast(e.message || 'Could not sign out.')
    }
  }, [toast])

  if (!isConfigured) return <NotConnected />
  if (checking) {
    return (
      <div className="main">
        <p className="lbl" style={{ padding: '24px 2px' }}>Checking your session…</p>
      </div>
    )
  }
  if (!session) return <SignIn />
  if (categories === null) {
    return (
      <div className="main">
        <p className="lbl" style={{ padding: '24px 2px' }}>Loading…</p>
      </div>
    )
  }
  if (categories.length === 0 && !catError) {
    return <FirstRun onDone={loadCategories} toast={toast} />
  }

  const onTab = TABS.some((t) => t.key === view)
  const category = onTab || view === 'inbox' ? null : categories.find((c) => c.id === view)
  const showChips = view === 'today' || Boolean(category)

  const onPhoneNavigate = (target) => {
    setReturnTo(null)
    setJump(null)
    setMoneySection('none')
    setView(target)
  }

  const resizeSidebar = (e) => {
    if (layout.platform !== 'mac' || !sidebarOpen) return
    startPaneResize(e, setSidebarWidth, {
      min: 200,
      max: 360,
      measure: (event) => event.clientX,
    })
  }

  const updateContentScrolled = (e) => {
    const root = e.currentTarget
    const splitPaneScrolled = Array.from(
      root.querySelectorAll('.split-list-scroll, .split-detail-pane'),
    ).some((pane) => pane.scrollTop > 0)
    const next = root.scrollTop > 0 || splitPaneScrolled
    setContentScrolled((current) => current === next ? current : next)
  }

  let body
  if (category) {
    body = isCashflow(category)
      ? <Money
          category={category}
          toast={toast}
          section={moneySection === 'none' ? 'cashflow' : moneySection}
          onSectionChange={setMoneySection}
          jump={jump}
          onJumpHandled={() => setJump(null)}
        />
      : <Category
          category={category}
          toast={toast}
          refreshKey={refreshKey}
          jump={jump}
          onJumpHandled={() => setJump(null)}
        />
  } else if (view === 'reminders') {
    body = <Reminders toast={toast} refreshKey={refreshKey} />
  } else if (view === 'calendar') {
    body = <Calendar toast={toast} refreshKey={refreshKey} />
  } else if (view === 'log') {
    body = <Log refreshKey={refreshKey} onOpenRecord={jumpToRecord} />
  } else if (view === 'search') {
    body = <Search />
  } else if (view === 'inbox') {
    body = <Inbox categories={categories} toast={toast} onChanged={refresh} />
  } else {
    body = (
      <Today categories={categories} toast={toast} refreshKey={refreshKey}
        onOpenInbox={() => setView('inbox')} />
    )
  }

  const moneyTitle = MONEY_SECTIONS.find((section) => section.key === (moneySection === 'none' ? 'cashflow' : moneySection))?.label
  const navTitle = category
    ? (isCashflow(category) ? moneyTitle : category.name)
    : view === 'inbox' ? 'Inbox' : TABS.find((tab) => tab.key === view)?.label || 'Today'
  const sidebarCurrent = category && isCashflow(category) && moneySection !== 'none' ? 'money' : view

  return (
    <div className={'app ' + layout.platform + (sidebarOpen ? ' sidebar-open' : ' sidebar-closed')}>
      {layout.platform === 'mac' ? (
        <Sidebar
          categories={categories}
          current={sidebarCurrent}
          moneySection={moneySection}
          onChange={onSidebarChange}
          collapsed={!sidebarOpen}
          width={sidebarWidth}
          onResize={resizeSidebar}
        />
      ) : null}

      <div className="workspace">
        <InstallHint />
        {layout.platform === 'mac' ? (
          <header className={'mac-toolbar' + (contentScrolled ? ' is-scrolled' : '')}>
            {returnTo ? (
              <button type="button" className="nav-back" onClick={goBack}>‹ Back</button>
            ) : null}
            <h1>{navTitle}</h1>
            <div className="mac-toolbar-actions">
              <div className="mac-toolbar-view-actions" id="mac-toolbar-view-actions" />
              <CaptureButton desktop toast={toast} onCaptured={refresh} />
              <AccountButton email={session.user?.email} onSignOut={handleSignOut} />
            </div>
          </header>
        ) : null}

        {layout.platform === 'ios' ? (
          <header className={'ios-nav' + (inlineTitleVisible ? ' title-visible' : '')}>
            {returnTo ? (
              <button type="button" className="nav-back" onClick={goBack}>‹ Back</button>
            ) : <span className="ios-nav-spacer" aria-hidden="true" />}
            <span className="ios-nav-title" aria-hidden={!inlineTitleVisible}>{navTitle}</span>
            <AccountButton email={session.user?.email} onSignOut={handleSignOut} />
          </header>
        ) : null}

        {layout.platform === 'ios' && showChips ? (
          <ChipRow categories={categories} current={category ? category.id : 'today'} onChange={onPhoneNavigate} />
        ) : null}

        <main
          ref={mainRef}
          className="main"
          onScrollCapture={updateContentScrolled}
        >
          {catError ? (
            <div className="notice warn">
              <p style={{ margin: 0, fontSize: 14 }}>Could not read your categories. {catError}</p>
            </div>
          ) : null}
          <JumpContext.Provider value={jumpToRecord}>{body}</JumpContext.Provider>
        </main>
      </div>

      {layout.platform === 'ios' ? <CaptureButton toast={toast} onCaptured={refresh} /> : null}

      {layout.platform === 'mac' ? <RowContextMenu key={view + ':' + moneySection} /> : null}
      {layout.platform === 'ios' ? <RowSwipe key={view + ':' + moneySection} /> : null}

      {msg ? (
        <div className="toast" role="status" aria-live="polite">{msg}</div>
      ) : null}

      {layout.platform === 'ios' ? <TabBar current={onTab ? view : 'today'} onChange={setView} /> : null}
    </div>
  )
}
